import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Box,
  Typography,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  Chip,
  InputAdornment,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import HistoryIcon from '@mui/icons-material/History';
import BoothSelectionMap from '../vendor/BoothSelectionMap';
import api, { categories as categoriesApi } from '../../services/api';
import { isAdminPlaceholderVendor } from '../../utils/vendorContact';

const formatVendorLabel = (option) => {
  if (!option) return '';
  const parts = [option.business_name];
  if (option.firstName || option.lastName) {
    parts.push(`${option.firstName || ''} ${option.lastName || ''}`.trim());
  }
  if (option.email) parts.push(option.email);
  return parts.filter(Boolean).join(' — ');
};

const AdminVendorRegistration = ({ open, onClose, vendor = null, mode = 'create' }) => {
  const [formData, setFormData] = useState({
    business_name: '',
    description: '',
    website: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    category_id: '',
    category_other_description: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_email: '',
  });
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  const [booths, setBooths] = useState([]);
  const [selectedBooths, setSelectedBooths] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [boothDialog, setBoothDialog] = useState(false);
  const [selectedBooth, setSelectedBooth] = useState(null);
  const [needsElectricity, setNeedsElectricity] = useState(false);

  // Returning-vendor search state. When existingVendor is set we treat
  // submission as an enrollment + update of an existing vendor instead
  // of creating a new vendors row.
  const [existingVendor, setExistingVendor] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const [searchOptions, setSearchOptions] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const searchRequestRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await categoriesApi.list();
        if (!cancelled) setCategoryOptions(Array.isArray(data) ? data : []);
      } catch (err) {
        console.warn('Failed to load categories', err);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedCategory = useMemo(
    () => categoryOptions.find((c) => String(c.id) === String(formData.category_id)),
    [categoryOptions, formData.category_id]
  );
  const isOtherSelected = selectedCategory?.slug === 'other';

  useEffect(() => {
    fetchBooths();
    if (mode === 'edit' && vendor) {
      setFormData({
        business_name: vendor.business_name || '',
        description: vendor.description || '',
        website: vendor.website || '',
        phone: vendor.phone || '',
        address: vendor.address || '',
        city: vendor.city || '',
        state: vendor.state || '',
        zip_code: vendor.zip_code || '',
        category_id: vendor.category_id ? String(vendor.category_id) : '',
        category_other_description: vendor.category_other_description || '',
        // Claimed accounts email users.email, but a correction may already
        // be sitting on contact_email. Prefer that so the next save copies
        // it onto the login. Unclaimed vendors only have contact_email.
        contact_first_name: isAdminPlaceholderVendor(vendor)
          ? (vendor.contact_first_name || '')
          : (vendor.contact_first_name || vendor.firstName || vendor.first_name || ''),
        contact_last_name: isAdminPlaceholderVendor(vendor)
          ? (vendor.contact_last_name || '')
          : (vendor.contact_last_name || vendor.lastName || vendor.last_name || ''),
        contact_email: isAdminPlaceholderVendor(vendor)
          ? (vendor.contact_email || vendor.email || '')
          : (vendor.contact_email || vendor.email || ''),
      });
      // Handle existing booth assignments for edit mode
      if (vendor.booth_details && Array.isArray(vendor.booth_details)) {
        setSelectedBooths(vendor.booth_details.map(booth => ({
          id: booth.booth_id || booth.id,  // ✅ Use booth_id (event_map ID), not registration ID
          cell_name: booth.booth_name,
          needs_electricity: booth.needs_electricity || false,
          is_corner: booth.is_corner,
          section: booth.section,
          x_coordinate: booth.x_coordinate,
          y_coordinate: booth.y_coordinate,
          cell_type: booth.cell_type
        })));
      }
    }
  }, [mode, vendor]);

  // Reset transient state whenever the dialog is reopened in create mode.
  useEffect(() => {
    if (open && mode === 'create') {
      setFormData({
        business_name: '',
        description: '',
        website: '',
        phone: '',
        address: '',
        city: '',
        state: '',
        zip_code: '',
        category_id: '',
        category_other_description: '',
        contact_first_name: '',
        contact_last_name: '',
        contact_email: '',
      });
      setSelectedBooths([]);
      setExistingVendor(null);
      setSearchInput('');
      setSearchOptions([]);
      setError(null);
    }
  }, [open, mode]);

  // Debounced search against /admin/vendors/search. We track an incrementing
  // request id so out-of-order responses never overwrite a newer query.
  useEffect(() => {
    if (mode !== 'create') return undefined;
    const query = searchInput.trim();
    if (existingVendor && query === formatVendorLabel(existingVendor)) {
      // The text in the box is the selected vendor's label; nothing to fetch.
      return undefined;
    }
    if (query.length < 2) {
      setSearchOptions([]);
      setSearchLoading(false);
      return undefined;
    }

    const requestId = ++searchRequestRef.current;
    setSearchLoading(true);

    const timer = setTimeout(async () => {
      try {
        const response = await api.get('/admin/vendors/search', { params: { q: query } });
        if (searchRequestRef.current === requestId) {
          setSearchOptions(Array.isArray(response.data) ? response.data : []);
        }
      } catch (err) {
        if (searchRequestRef.current === requestId) {
          console.warn('Vendor search failed:', err);
          setSearchOptions([]);
        }
      } finally {
        if (searchRequestRef.current === requestId) {
          setSearchLoading(false);
        }
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [searchInput, mode, existingVendor]);

  const fetchBooths = async () => {
    try {
      const response = await api.get('/admin/booths');
      setBooths(response.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch booths');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Reset the Other description if the vendor switches categories so we
      // don't ship stale text under a non-Other selection.
      ...(name === 'category_id' ? { category_other_description: '' } : {}),
    }));
  };

  const buildVendorPayload = () => ({
    ...formData,
    category_id: formData.category_id ? Number(formData.category_id) : null,
    category_other_description: isOtherSelected
      ? formData.category_other_description.trim()
      : null,
    contact_first_name: formData.contact_first_name.trim() || null,
    contact_last_name: formData.contact_last_name.trim() || null,
    contact_email: formData.contact_email.trim().toLowerCase() || null,
  });

  const handleSelectExistingVendor = (vendorOption) => {
    if (!vendorOption) {
      setExistingVendor(null);
      return;
    }
    setExistingVendor(vendorOption);
    setFormData({
      business_name: vendorOption.business_name || '',
      description: vendorOption.description || '',
      website: vendorOption.website || '',
      phone: vendorOption.phone || '',
      address: vendorOption.address || '',
      city: vendorOption.city || '',
      state: vendorOption.state || '',
      zip_code: vendorOption.zip_code || '',
      category_id: vendorOption.category_id ? String(vendorOption.category_id) : '',
      category_other_description: vendorOption.category_other_description || '',
    });
    setError(null);
  };

  const handleClearExistingVendor = () => {
    setExistingVendor(null);
    setSearchInput('');
    setSearchOptions([]);
    setFormData({
      business_name: '',
      description: '',
      website: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      category_id: '',
      category_other_description: '',
      contact_first_name: '',
      contact_last_name: '',
      contact_email: '',
    });
  };

  const handleBoothSelect = (boothData) => {
    // Check if this booth is already selected
    if (selectedBooths.some(booth => booth.id === boothData.id)) {
      setError('This booth has already been selected');
      return;
    }

    // Check if we've reached the limit of 3 booths
    if (selectedBooths.length >= 3) {
      setError('You can only select up to 3 booths per vendor');
      return;
    }

    setSelectedBooth(boothData);
    setBoothDialog(true);
  };

  const handleBoothRegistrationSubmit = () => {
    const newBooth = {
      ...selectedBooth,
      needs_electricity: needsElectricity
    };
    setSelectedBooths([...selectedBooths, newBooth]);
    setBoothDialog(false);
    setSelectedBooth(null);
    setNeedsElectricity(false);
    setError('');
  };

  const handleRemoveBooth = (boothId) => {
    setSelectedBooths(selectedBooths.filter(booth => booth.id !== boothId));
  };

  const calculateBoothPrice = (booth) => {
    if (!booth) return 0;
    let basePrice = booth.is_corner ? 80 : 70; // $80 for corner, $70 for regular
    if (booth.needs_electricity) basePrice += 5; // Add $5 for electricity
    return basePrice;
  };

  const calculateTotalPrice = () => {
    return selectedBooths.reduce((total, booth) => total + calculateBoothPrice(booth), 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    if (!formData.category_id) {
      setError('Please choose a primary craft category.');
      setLoading(false);
      return;
    }
    if (isOtherSelected && !formData.category_other_description.trim()) {
      setError('Please describe the vendor\'s craft (since Other was chosen).');
      setLoading(false);
      return;
    }

    const vendorPayload = buildVendorPayload();

    try {
      if (mode === 'create') {
        let vendorData;

        if (existingVendor) {
          // Returning vendor — re-use the existing vendors row, refresh
          // any edited profile fields, and ensure an enrollment exists
          // for the current festival.
          await api.post(`/admin/vendors/${existingVendor.id}/enroll-current`);
          const updateResponse = await api.put(
            `/admin/vendors/${existingVendor.id}`,
            vendorPayload
          );
          vendorData = updateResponse.data;
        } else {
          const vendorResponse = await api.post('/admin/vendors', vendorPayload);
          vendorData = vendorResponse.data;
        }

        for (const booth of selectedBooths) {
          await api.post('/admin/booth-assignments', {
            vendorId: vendorData.id,
            boothId: booth.id,
            needsElectricity: booth.needs_electricity,
          });
        }
      } else {
        await api.put(`/admin/vendors/${vendor.id}`, vendorPayload);

        // Handle booth assignments for edit mode - smart incremental updates
        // Ensure we're comparing the same ID types (booth_id, not registration_id)
        const existingBoothIds = vendor.booth_details ? 
          vendor.booth_details.map(booth => {
            // Always use the actual booth_id for comparison
            return booth.booth_id || booth.id;
          }).filter(id => id !== undefined && id !== null) : [];
        
        // Filter out invalid booth selections and extract proper IDs
        const validSelectedBooths = selectedBooths.filter(booth => 
          booth && (booth.id !== undefined && booth.id !== null)
        );
        
        const selectedBoothIds = validSelectedBooths.map(booth => {
          // selectedBooths should contain booth_id (event_map.id)
          return booth.id;
        });

        // Find booths to remove (in existing but not in selected)
        const boothsToRemove = vendor.booth_details ? 
          vendor.booth_details.filter(booth => {
            const boothId = booth.booth_id || booth.id;
            return boothId && !selectedBoothIds.includes(boothId);
          }) : [];

        // Find booths to add (in selected but not in existing)
        const boothsToAdd = validSelectedBooths.filter(booth => {
          return booth.id && !existingBoothIds.includes(booth.id);
        });

        console.log('Booth assignment changes:', {
          existing: existingBoothIds,
          selected: selectedBoothIds,
          toRemove: boothsToRemove.map(b => b.booth_id || b.id),
          toAdd: boothsToAdd.map(b => b.id)
        });

        // Debug: Show raw data to understand undefined values
        console.log('Debug booth data:', {
          rawSelectedBooths: selectedBooths,
          validSelectedBooths: validSelectedBooths,
          existingBoothDetails: vendor.booth_details
        });

        for (const booth of boothsToRemove) {
          const boothIdToDelete = booth.booth_id || booth.id;
          try {
            await api.delete(`/admin/booth-assignments/${boothIdToDelete}`);
          } catch (removeErr) {
            console.warn(`Failed to remove booth ${boothIdToDelete}:`, removeErr.response?.data || removeErr.message);
          }
        }

        for (const booth of boothsToAdd) {
          try {
            await api.post('/admin/booth-assignments', {
              vendorId: vendor.id,
              boothId: booth.id,
              needsElectricity: booth.needs_electricity,
            });
          } catch (addErr) {
            throw new Error(addErr.response?.data?.error || `Failed to assign booth ${booth.cell_name || booth.id}`);
          }
        }
      }

      onClose(true);
    } catch (err) {
      console.error('Error in vendor operation:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save vendor');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {mode === 'create'
          ? (existingVendor ? `Enroll Returning Vendor — ${existingVendor.business_name}` : 'Add New Vendor')
          : 'Edit Vendor'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          <Grid container spacing={3}>
            {/* Vendor Profile Form */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Vendor Profile
              </Typography>

              <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
                Vendor contact
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                {mode === 'edit' && vendor && !isAdminPlaceholderVendor(vendor)
                  ? 'This is the vendor login and the address festival emails are sent to.'
                  : 'Optional. Used for display and emails until the vendor claims their account.'}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First name"
                    name="contact_first_name"
                    value={formData.contact_first_name}
                    onChange={handleInputChange}
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last name"
                    name="contact_last_name"
                    value={formData.contact_last_name}
                    onChange={handleInputChange}
                    margin="dense"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Email"
                    name="contact_email"
                    type="email"
                    value={formData.contact_email}
                    onChange={handleInputChange}
                    margin="dense"
                  />
                </Grid>
              </Grid>

              {mode === 'create' && (
                <Box sx={{ mb: 2 }}>
                  <Autocomplete
                    freeSolo
                    fullWidth
                    options={searchOptions}
                    loading={searchLoading}
                    filterOptions={(x) => x}
                    inputValue={searchInput}
                    onInputChange={(_event, value, reason) => {
                      setSearchInput(value);
                      if (reason === 'clear') {
                        handleClearExistingVendor();
                      }
                    }}
                    value={existingVendor}
                    isOptionEqualToValue={(option, val) =>
                      option && val && option.id === val.id
                    }
                    getOptionLabel={(option) =>
                      typeof option === 'string' ? option : formatVendorLabel(option)
                    }
                    onChange={(_event, value) => {
                      if (value && typeof value !== 'string') {
                        handleSelectExistingVendor(value);
                        setSearchInput(formatVendorLabel(value));
                      } else if (!value) {
                        handleClearExistingVendor();
                      }
                    }}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        <Box sx={{ width: '100%' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {option.business_name}
                            </Typography>
                            {option.already_enrolled && (
                              <Chip
                                size="small"
                                color="warning"
                                label="Already enrolled"
                              />
                            )}
                            {!option.already_enrolled && option.last_festival_year && (
                              <Chip
                                size="small"
                                variant="outlined"
                                icon={<HistoryIcon fontSize="small" />}
                                label={`Last: ${option.last_festival_year}`}
                              />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {[
                              `${option.firstName || ''} ${option.lastName || ''}`.trim(),
                              option.email,
                              option.phone,
                            ].filter(Boolean).join(' · ')}
                          </Typography>
                        </Box>
                      </li>
                    )}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search returning vendors"
                        placeholder="Type a business name, email, owner, or phone…"
                        helperText={
                          existingVendor
                            ? null
                            : 'Returning vendor? Search to auto-fill their profile.'
                        }
                        InputProps={{
                          ...params.InputProps,
                          startAdornment: (
                            <>
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                              {params.InputProps.startAdornment}
                            </>
                          ),
                          endAdornment: (
                            <>
                              {searchLoading ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />

                  {existingVendor && (
                    <Alert
                      severity={existingVendor.already_enrolled ? 'warning' : 'info'}
                      sx={{ mt: 1 }}
                      action={
                        <Button color="inherit" size="small" onClick={handleClearExistingVendor}>
                          Clear
                        </Button>
                      }
                    >
                      {existingVendor.already_enrolled
                        ? `${existingVendor.business_name} is already enrolled this year. Saving will update their profile and add any newly selected booths.`
                        : `Re-enrolling ${existingVendor.business_name}${
                            existingVendor.last_festival_year
                              ? ` (last festival: ${existingVendor.last_festival_year})`
                              : ''
                          }. Edits below will update their existing profile.`}
                    </Alert>
                  )}
                </Box>
              )}

              <form onSubmit={handleSubmit}>
                <TextField
                  fullWidth
                  label="Business Name"
                  name="business_name"
                  value={formData.business_name}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                />
                <TextField
                  select
                  fullWidth
                  label="Primary Craft Category"
                  name="category_id"
                  value={formData.category_id}
                  onChange={handleInputChange}
                  required
                  margin="normal"
                  disabled={categoriesLoading}
                  helperText={
                    categoriesLoading
                      ? 'Loading categories…'
                      : selectedCategory?.description ||
                        'Pick the category that best describes this vendor\'s craft. Choose "Other" if none fit.'
                  }
                >
                  {categoryOptions.map((cat) => (
                    <MenuItem key={cat.id} value={String(cat.id)}>
                      {cat.name}
                    </MenuItem>
                  ))}
                </TextField>
                {isOtherSelected && (
                  <TextField
                    fullWidth
                    label="Describe their craft"
                    name="category_other_description"
                    value={formData.category_other_description}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                    inputProps={{ maxLength: 120 }}
                    helperText="Short label since Other was chosen (e.g. Resin Art, Leatherworking). Max 120 characters."
                  />
                )}
                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  multiline
                  rows={4}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Website"
                  name="website"
                  value={formData.website}
                  onChange={handleInputChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  margin="normal"
                />
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  margin="normal"
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleInputChange}
                      margin="normal"
                    />
                  </Grid>
                </Grid>
              </form>
            </Grid>

            {/* Booth Selection */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Booth Selection
              </Typography>
              
              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Selected Booths Display */}
              {selectedBooths.length > 0 && (
                <Paper elevation={0} sx={{ p: 2, mb: 3, backgroundColor: '#f5f5f5' }}>
                  <Typography variant="subtitle1" gutterBottom>Selected Booths:</Typography>
                  <List dense>
                    {selectedBooths.map((booth) => (
                      <ListItem key={booth.id}>
                        <ListItemText
                          primary={`Booth ${booth.cell_name}`}
                          secondary={`${booth.is_corner ? 'Corner' : 'Regular'} Booth - ${booth.needs_electricity ? 'With' : 'No'} Electricity - $${calculateBoothPrice(booth)}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton edge="end" onClick={() => handleRemoveBooth(booth.id)}>
                            <DeleteIcon />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                  <Box sx={{ mt: 2, borderTop: '1px solid #ddd', pt: 2 }}>
                    <Typography variant="subtitle1">
                      Total Price: ${calculateTotalPrice()}
                    </Typography>
                  </Box>
                </Paper>
              )}

              {/* Booth Map */}
              <Paper 
                elevation={3} 
                sx={{ 
                  p: 2, 
                  height: '400px', 
                  overflow: 'auto',
                  backgroundColor: '#f5f5f5'
                }}
              >
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Click on an available booth to select it. Blue booths are available, grey booths are taken.
                    {selectedBooths.length < 3 && ' You can select up to 3 booths.'}
                  </Typography>
                </Box>
                <BoothSelectionMap 
                  onSelect={handleBoothSelect}
                  initialMapData={booths}
                  selectedBooths={selectedBooths}
                />
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          disabled={loading || !formData.business_name || !formData.category_id}
        >
          {loading
            ? 'Saving...'
            : mode === 'create'
              ? (existingVendor ? 'Enroll Vendor' : 'Create Vendor')
              : 'Save Changes'}
        </Button>
      </DialogActions>

      {/* Booth Selection Dialog */}
      <Dialog
        open={boothDialog}
        onClose={() => setBoothDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Select Booth {selectedBooth?.cell_name}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>
              Booth Details:
            </Typography>
            <Box sx={{ pl: 2, mb: 2 }}>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Booth Number: {selectedBooth?.cell_name}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Location: {selectedBooth?.x_coordinate}, {selectedBooth?.y_coordinate}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Section: {selectedBooth?.section}
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                • Corner Booth: {selectedBooth?.is_corner ? 'Yes' : 'No'}
              </Typography>
            </Box>
            <Box sx={{ mt: 3, mb: 3 }}>
              <FormControlLabel
                control={<Checkbox checked={needsElectricity} onChange={e => setNeedsElectricity(e.target.checked)} color="primary" />}
                label="I need electricity at my booth (+$5)"
              />
            </Box>
            <Box sx={{ 
              mt: 3, 
              p: 2, 
              backgroundColor: '#f5f5f5', 
              borderRadius: 1,
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Estimated Fee: ${calculateBoothPrice(selectedBooth)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedBooth?.is_corner ? '• Corner booth ($80)' : '• Regular booth ($70)'}
                {needsElectricity && ' • Electricity (+$5)'}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBoothDialog(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={handleBoothRegistrationSubmit}
            disabled={!selectedBooth}
          >
            Add Booth
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default AdminVendorRegistration; 